import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Card, Empty, Spin, Tabs, Tag, Tooltip, Typography} from 'antd';
import {ReloadOutlined} from '@ant-design/icons';
import type {EquityHistoryResponse} from '../../services/botService';
import {botService} from '../../services/botService';
import EquityBreakdownCharts from './EquityBreakdownCharts';

const {Text} = Typography;
const STATUS_CARD_HEIGHT = 300;
const TAB_CONTENT_HEIGHT = 250;

interface LightweightEquityChartProps {
    apiKeyId: number;
    onDataLoad?: () => void;
}

const LightweightEquityChart: React.FC<LightweightEquityChartProps> = ({apiKeyId, onDataLoad}) => {
    const [equityData, setEquityData] = useState<EquityHistoryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [nonPnlBaselineVisible, setNonPnlBaselineVisible] = useState(true);

    const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchEquityHistory = useCallback(async () => {
        if (!apiKeyId) return;
        setLoading(true);
        try {
            const response = await botService.getEquityHistory(apiKeyId, 500);
            if (response.success && response.data) {
                setEquityData(response.data);
                setError('');
                onDataLoad?.();
            } else {
                setEquityData(prev => prev);
                setError(response.message || '获取权益数据失败');
            }
        } catch {
            setError('获取权益数据失败');
        } finally {
            setLoading(false);
        }
    }, [apiKeyId, onDataLoad]);

    useEffect(() => {
        fetchEquityHistory();
        refreshTimerRef.current = setInterval(fetchEquityHistory, 30000);
        return () => {
            if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
        };
    }, [fetchEquityHistory]);

    const handleRefresh = useCallback(async () => {
        if (!apiKeyId) return;
        setRefreshing(true);
        try {
            await botService.refreshEquityHistory(apiKeyId);
            await fetchEquityHistory();
        } catch {
        } finally {
            setRefreshing(false);
        }
    }, [apiKeyId, fetchEquityHistory]);

    const formatPriceTime = (isoStr?: string) => {
        if (!isoStr) return '--';
        try {
            const d = new Date(isoStr);
            return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
        } catch {
            return isoStr;
        }
    };

    const latestBreakdown = equityData?.latestBreakdown;
    const hasPoints = !loading && !!equityData?.points?.length;
    const showCharts = hasPoints;
    const dexNoBreakdown = latestBreakdown?.stablecoinEquity == null && latestBreakdown?.otherAssetEquity == null;

    const isFailureStatus = equityData?.quality === 'FETCH_FAILED' || equityData?.quality === 'CONFIG_REQUIRED';
    const showFailedEmpty = !hasPoints && isFailureStatus;

    const getQualityTag = () => {
        switch (equityData?.quality) {
            case 'UNSUPPORTED':
                return <Tag color="orange">暂不支持</Tag>;
            case 'FETCH_FAILED':
                return <Tag color="red">当前采集失败</Tag>;
            case 'CONFIG_REQUIRED':
                return <Tag color="orange">需要配置</Tag>;
            case 'ESTIMATED':
                return <Tag color="yellow">估算</Tag>;
            default:
                return null;
        }
    };

    const detailContent = latestBreakdown?.assets && latestBreakdown.assets.length > 0 ? (
        <table style={{width: '100%', fontSize: 11, borderCollapse: 'collapse'}}>
            <thead>
            <tr style={{color: '#666', borderBottom: '1px solid #2a2a2a'}}>
                <th style={{padding: '4px 8px', textAlign: 'left'}}>币种</th>
                <th style={{padding: '4px 8px', textAlign: 'left'}}>分类</th>
                <th style={{padding: '4px 8px', textAlign: 'right'}}>总量</th>
                <th style={{padding: '4px 8px', textAlign: 'right'}}>可用</th>
                <th style={{padding: '4px 8px', textAlign: 'right'}}>冻结</th>
                <th style={{padding: '4px 8px', textAlign: 'right'}}>价格 USD</th>
                <th style={{padding: '4px 8px', textAlign: 'right'}}>估值 USD</th>
                <th style={{padding: '4px 8px', textAlign: 'left'}}>价格来源</th>
                <th style={{padding: '4px 8px', textAlign: 'left'}}>估值时间</th>
            </tr>
            </thead>
            <tbody>
            {latestBreakdown.assets.map((a, i) => {
                const isParity = a.priceSource === 'STABLECOIN_PARITY';
                const priceTimeExpired = a.priceTime && (Date.now() - new Date(a.priceTime).getTime()) > 60000;
                return (
                    <tr key={i} style={{borderBottom: '1px solid #1a1a1a'}}>
                        <td style={{padding: '4px 8px', color: '#ddd', whiteSpace: 'nowrap'}}>{a.currency}</td>
                        <td style={{
                            padding: '4px 8px',
                            color: '#666',
                            whiteSpace: 'nowrap'
                        }}>{a.assetClass || '--'}</td>
                        <td style={{
                            padding: '4px 8px',
                            textAlign: 'right',
                            color: '#ccc'
                        }}>{a.totalAmount?.toFixed(4)}</td>
                        <td style={{
                            padding: '4px 8px',
                            textAlign: 'right',
                            color: '#00d97e'
                        }}>{a.availableAmount?.toFixed(4)}</td>
                        <td style={{
                            padding: '4px 8px',
                            textAlign: 'right',
                            color: '#f5a623'
                        }}>{a.frozenAmount?.toFixed(4)}</td>
                        <td style={{padding: '4px 8px', textAlign: 'right', color: isParity ? '#00d97e' : '#999'}}>
                            {isParity ? '1.00' : a.priceUsd?.toFixed(2)}
                        </td>
                        <td style={{
                            padding: '4px 8px',
                            textAlign: 'right',
                            color: '#F0B429'
                        }}>{a.valueUsd?.toFixed(2)}</td>
                        <td style={{padding: '4px 8px', color: '#666', fontSize: 10, whiteSpace: 'nowrap'}}>
                            {isParity ? '按 1 USD 估值' : a.priceSource}
                        </td>
                        <td style={{padding: '4px 8px', color: '#666', fontSize: 10, whiteSpace: 'nowrap'}}>
                            {a.priceTime ? formatPriceTime(a.priceTime) : '--'}
                            {priceTimeExpired && <span style={{color: '#f5a623', marginLeft: 4}}>可能过期</span>}
                        </td>
                    </tr>
                );
            })}
            </tbody>
        </table>
    ) : (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text
                style={{color: '#999'}}>{dexNoBreakdown ? '钱包资产分项暂不可用' : '暂无钱包资产明细'}</Text>}
        />
    );

    const chartContent = showCharts ? (
        <EquityBreakdownCharts
            points={equityData!.points}
            quality={equityData!.quality}
            dexNoBreakdown={dexNoBreakdown}
            showNonPnlBaseline={nonPnlBaselineVisible}
            onToggleNonPnlBaseline={setNonPnlBaselineVisible}
        />
    ) : (
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: TAB_CONTENT_HEIGHT}}>
            {loading ? <Spin size="large"/> : (
                <Empty description={<Text style={{color: '#d9d9d9'}}>
                    {showFailedEmpty
                        ? (equityData?.quality === 'CONFIG_REQUIRED' ? '请在API连接中配置钱包地址' : '权益数据暂不可用')
                        : (equityData?.quality === 'UNSUPPORTED' ? '当前数据源暂不支持权益查询' : (error || '暂无数据'))}
                </Text>} image={Empty.PRESENTED_IMAGE_SIMPLE}/>
            )}
        </div>
    );

    return (
        <Card
            size="small"
            style={{height: STATUS_CARD_HEIGHT, backgroundColor: '#0d1117', border: '1px solid rgba(255,255,255,0.13)'}}
            styles={{body: {height: STATUS_CARD_HEIGHT, padding: '0 12px', overflow: 'hidden'}}}
        >
            <Tabs
                defaultActiveKey="chart"
                size="small"
                tabBarStyle={{marginBottom: 6, minHeight: 36}}
                tabBarExtraContent={{
                    right: (
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            {getQualityTag()}
                            <Tooltip title="刷新权益数据">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ReloadOutlined/>}
                                    loading={refreshing}
                                    onClick={handleRefresh}
                                    aria-label="刷新权益数据"
                                    style={{color: '#999'}}
                                />
                            </Tooltip>
                        </div>
                    )
                }}
                items={[
                    {key: 'chart', label: '权益图表', children: chartContent},
                    {
                        key: 'detail',
                        label: latestBreakdown?.assets?.length ? `资产明细 (${latestBreakdown.assets.length})` : '资产明细',
                        children: (
                            <div style={{height: TAB_CONTENT_HEIGHT, overflow: 'auto'}}>
                                {detailContent}
                                {latestBreakdown?.warnings && latestBreakdown.warnings.length > 0 && (
                                    <div style={{
                                        marginTop: 12,
                                        padding: '6px 10px',
                                        background: '#2a1a1a',
                                        borderRadius: 4,
                                        border: '1px solid #4a2020'
                                    }}>
                                        {latestBreakdown.warnings.map((w, i) => (
                                            <div key={i} style={{color: '#f5a623', fontSize: 11}}>警告: {w}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    },
                ]}
            />
        </Card>
    );
};

export default LightweightEquityChart;
